import React, { useMemo } from 'react';
import './TotalPortfolioCard.css';
import './LegalAccountsCard.css';

export interface LegalAccountsMetrics {
  total_loans: number;
  jdg_loans: number;
  gdg_loans: number;
  wip_loans: number;
  pct_jdg: number;
  pct_gdg: number;
  pct_wip: number;
}

export interface AttorneyOfficeRow {
  attorney_office: string;
  account_count: number;
  pct_of_total?: number;
  jdg?: number;
  gdg?: number;
  wip?: number;
}

interface LegalAccountsCardProps {
  metrics: LegalAccountsMetrics | null;
  offices: AttorneyOfficeRow[];
  loading?: boolean;
  onAttorneyOfficeClick?: (office: AttorneyOfficeRow) => void;
}

const LegalAccountsCard: React.FC<LegalAccountsCardProps> = ({
  metrics,
  offices,
  loading = false,
  onAttorneyOfficeClick,
}) => {
  const sortedOffices = useMemo(() => {
    return [...offices].sort((a, b) => b.account_count - a.account_count);
  }, [offices]);

  const safePct = (pct: unknown, numerator: number, denominator: number): number => {
    const pctNum = typeof pct === 'number' ? pct : Number(pct);
    if (Number.isFinite(pctNum)) return Math.max(0, Math.min(100, pctNum));
    if (!denominator) return 0;
    return Math.max(0, Math.min(100, (numerator / denominator) * 100));
  };

  const formatPercent = (value: number): string => `${value.toFixed(1)}%`;

  const officeCount = sortedOffices.length;

  return (
    <div className="total-portfolio-card legal-accounts-card">
      <div className="total-portfolio-header">
        <div className="total-portfolio-icon">
          <span className="portfolio-icon">⚖️</span>
        </div>
        <div className="total-portfolio-info">
          <h3 className="total-portfolio-name">Legal Accounts</h3>
          <p className="total-portfolio-subtitle">All Legal Accounts Combined</p>
        </div>
      </div>

      <div className="total-portfolio-metrics">
        {!metrics ? (
          <p className="legal-accounts-empty">
            {loading ? 'Loading legal portfolio…' : 'No legal metrics available.'}
          </p>
        ) : (
          <>
            <div className="metric-row">
              <div className="metric-item">
                <span className="metric-label">Total loans</span>
                <span className="metric-value">{metrics.total_loans.toLocaleString()}</span>
              </div>
              <div className="metric-item">
                <span className="metric-label">Attorney offices</span>
                <span className="metric-value">{officeCount.toLocaleString()}</span>
              </div>
            </div>

            <div className="legal-status-section-label">
              Status 1 mix — % of legal loans
            </div>
            <div className="metric-row metric-row--triple legal-status-row">
              <div className="metric-item">
                <span className="metric-label">JDG</span>
                <span className="metric-value legal-pct-jdg">
                  {formatPercent(safePct(metrics.pct_jdg, metrics.jdg_loans, metrics.total_loans))}
                </span>
                <span className="legal-status-loan-count">
                  {metrics.jdg_loans.toLocaleString()} loans
                </span>
              </div>
              <div className="metric-item">
                <span className="metric-label">GDG</span>
                <span className="metric-value legal-pct-gdg">
                  {formatPercent(safePct(metrics.pct_gdg, metrics.gdg_loans, metrics.total_loans))}
                </span>
                <span className="legal-status-loan-count">
                  {metrics.gdg_loans.toLocaleString()} loans
                </span>
              </div>
              <div className="metric-item">
                <span className="metric-label">WIP</span>
                <span className="metric-value legal-pct-wip">
                  {formatPercent(safePct(metrics.pct_wip, metrics.wip_loans, metrics.total_loans))}
                </span>
                <span className="legal-status-loan-count">
                  {metrics.wip_loans.toLocaleString()} loans
                </span>
              </div>
            </div>

            <div className="past-due-breakdown legal-office-breakdown">
              <div className="legal-office-breakdown-title">By attorney office</div>
              <div
                className="legal-office-list-scroll"
                role="region"
                aria-label="Attorney offices list"
              >
                {sortedOffices.length === 0 ? (
                  <p className="legal-accounts-empty legal-accounts-empty--compact">
                    {loading ? 'Loading offices…' : 'No attorney offices in this portfolio.'}
                  </p>
                ) : (
                  sortedOffices.map((row, idx) => (
                    <div
                      key={`${row.attorney_office}-${idx}`}
                      className={`breakdown-item legal-office-row${onAttorneyOfficeClick ? ' legal-office-row--clickable' : ''}`}
                      onClick={() => onAttorneyOfficeClick?.(row)}
                      onKeyDown={(e) => {
                        if (onAttorneyOfficeClick && (e.key === 'Enter' || e.key === ' ')) {
                          e.preventDefault();
                          onAttorneyOfficeClick(row);
                        }
                      }}
                      role={onAttorneyOfficeClick ? 'button' : undefined}
                      tabIndex={onAttorneyOfficeClick ? 0 : undefined}
                    >
                      <span className="breakdown-label legal-office-name" title={row.attorney_office}>
                        {row.attorney_office}
                      </span>
                      <span className="breakdown-value legal-office-count">
                        {row.account_count.toLocaleString()}
                        <span className="legal-office-subcounts">
                          {row.jdg != null && row.gdg != null && row.wip != null && (
                            <>
                              {' '}
                              <span className="legal-office-subcounts-inner">
                                (JDG {row.jdg} · GDG {row.gdg} · WIP {row.wip})
                              </span>
                            </>
                          )}
                        </span>
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {loading && (
        <div className="loading-overlay">
          <div className="spinner"></div>
        </div>
      )}

      <div className="click-hint">
        <span>
          Click an attorney office to open accounts.
        </span>
      </div>
    </div>
  );
};

export default LegalAccountsCard;
